import { format, isValid as isDateTimeValid, parse } from "date-fns";
import { toDate, utcToZonedTime } from "date-fns-tz";

import { merge } from "lodash";
import { postData } from "../../api/componentAPIs";
import constant from "../../appConstants";
import { RMAXLogger } from "../../services/LoggerService/RMAXLogger";
import GRID_CONSTANTS from "./GridConstants";
import { getDisplayServiceLevels, toPriorityList } from "../modules/shared/serviceLevelUtils";

// https://en.wikipedia.org/wiki/List_of_tz_database_time_zones 
// Timezones based on US daylight savings
const TIMEZONES ={
    CST : "America/Chicago",
    EST : "America/New_York"
}

// TODO : date fns library needs to be updated and we need to handle breaking changes
const formatDate = (params) => {
    if (params && params.value) {
        return format(getUTCDate(params.value), constant.DATETYPES.DATE.format);
    }
    return "";
}

const formatDateYear=(params) => {
    if (params && params.value) {
        return format(getUTCDate(params.value), constant.DATETYPES.DATEYEAR.format);
    }
    return "";
}

const formatMonthDateYear=(params) => {
    if (params && params.value) {
        return format(getUTCDate(params.value), constant.DATETYPES.MONTHDATEYEAR.format);
    }
    return "";
}

const formatMonthDateYearWithoutUTC=(params) => {
  if (params && params.value) {
      return format(params.value, constant.DATETYPES.MONTHDATEYEAR.format);
  }
  return "";
}

const formatTime = (params) => {
    if (params && params.value) {
        return format(getUTCDate(params.value), constant.DATETYPES.TIME.format);
    }
    return "";

}


const formatDateTime = (params) => {
    if (params && params.value) {
        return format(getUTCDate(params.value), constant.DATETYPES.DATETIME.format);
    }
    return "";
}

const formatDateTimeWithoutUTCConversion = (params) => {
    if (params && params.value) {
      return format(new Date(params.value), constant.DATETYPES.DATETIME.format);
    }
    return "";
  };

const formatESTDateTime = (params) => {
    if (params && params.value) {
        const estDateTime = utcToZonedTime(params.value, TIMEZONES.EST ); // America/New_York gets EST time based on daylight savings https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
        return format(estDateTime, params.dateTimeFormat ? params.dateTimeFormat: constant.DATETYPES.DATETIME.format);
    }
    return "";
}

const formatCSTTimeStringToEST =(params) => {
    if (params && params.value) {
        const timeString = params.value.padStart(4,0);
        const dateTimeStringFormat =  "yyyy-MM-dd'T'HH:mm:00.000";
        const formattedDateTimeString  = format(parse(timeString, "HHmm", new Date()), dateTimeStringFormat);
      if (isDateTimeValid(formattedDateTimeString)) {
        const cstDateTime = toDate(formattedDateTimeString, { timeZone: TIMEZONES.CST });
        const estDateTime = utcToZonedTime(cstDateTime, TIMEZONES.EST);
        return format(estDateTime, constant.DATETYPES.TIME.format);
      }
    }
    return "";
}


const minutesToTime = params => {
    if (params && params.value !== null && params.value !== undefined) {
        const minutes = parseInt(params.value);
        const hour = minutes / 60;
        const min = minutes % 60;
        const newDate = new Date();
        newDate.setHours(hour);
        newDate.setMinutes(min);
        return format(newDate, constant.DATETYPES.TIME.format);
    }
    return null;
}

// automatic convertion from datetime to minutes
function timeToMinutes(params) {
    if (params) {
        let newValue = null
        if (params.newValue) {
            const updatedDate = getUTCDate(params.newValue);
            newValue = updatedDate.getHours() * 60 + updatedDate.getMinutes();
        }
        params.data[params.colDef.field] = newValue;
    }
    return true;
}

// Converts datetime string to minutes , used for bulk updates
function convertTimeToMinute(time) {
    if (time) {
        const updatedDate = getUTCDate(time);
        return updatedDate.getHours() * 60 + updatedDate.getMinutes();
    }
    return null;
}


const roundingNumbers = params => {
    if (params && params.value) {
        return params.value.toFixed(2);

    }
    return "";
}

// Rounds to max 2 decimals without padding whole/short values with trailing zeros (3 -> "3", 3.5 -> "3.5", 3.456 -> "3.46")
const roundingNumbersWithoutTrailingZeros = ({ value } = {}) =>
  value === null || value === undefined || value === ""
    ? ""
    : String(Math.round(Number(value) * 100) / 100);

function setTimeFromDateTime(params) {
    if (params) {
        let newValue = null;
        if (params.newValue) {
            newValue = format(getUTCDate(params.newValue), constant.DATETYPES.TIME.format);
        }
        params.data[params.colDef.field] = newValue;
    }
    return true;
}

function convertDateTimeToTimeString(dateTimeString) {
    if (dateTimeString) {
        const dateTime = getUTCDate(dateTimeString);
        return format(dateTime, constant.DATETYPES.TIME.format);
    }
    return null;
}

const formateColumnDefsValues = (column) => {
  return {
    headerName: column.colDef.defaultHeaderName ?? column.colDef.headerName, // use defaultHeaderName while saving if it is present to avoid resetting column properties in userConfig
    hide: !column.visible,
    pinned: column.pinned ? "left" : null,
  };
};

const getColumnDefs = (columns) => {
  return columns.reduce((columnDefs, column) => {
    if (column.parent?.groupId) {
      const existingHeader = columnDefs.find((col) => col.headerName === column.parent.groupId);
      if (existingHeader) {
        existingHeader.children = existingHeader.children
          ? [...existingHeader.children, formateColumnDefsValues(column)]
          : [formateColumnDefsValues(column)];
      } else if (column.parent.children.length > 1) {
        columnDefs.push({
          children: [formateColumnDefsValues(column)],
          headerName: column.parent.groupId,
          hide: !column.parent.visible
        });
      } else {
        columnDefs.push(formateColumnDefsValues(column));
      }
    } else {
      columnDefs.push(formateColumnDefsValues(column));
    }
    return columnDefs;
  }, []);
};

const saveGridColumnState = ({ gridName, columns, userColumnPref }) => {
    const columnDefs = getColumnDefs(columns);
    if (!userColumnPref) {
        userColumnPref = {}
    }
    userColumnPref[`${gridName}_Config`] = { columns: columnDefs };
    postData("SetUserPreferences", {
        columnOrderPref: JSON.stringify(userColumnPref)
    }).then(response => {
        if(response && Math.trunc(response.status/100) === 2) {
            console.log("user column preferences saved", gridName);
        }
    },
        error => {
            RMAXLogger.error(`Error while saving column preferences ${gridName}`, {
              error
            })
        }
    )
    return userColumnPref;
}

function isTimeValid(timeString) {
    const dateTime = parse(timeString, constant.DATETYPES.TIME.format, new Date());
    return isDateTimeValid(dateTime);
}

function mergeUserConfig({ defaultConfig, userConfig }) {
    if (userConfig && typeof userConfig === "object" && Object.keys(userConfig).length > 0) {
        if (userConfig[`${defaultConfig.title}_Config`]) {
            const mergedColumns = mergeColumnConfig(defaultConfig.columns, userConfig[`${defaultConfig.title}_Config`].columns);
            const mergedToggles = mergeGridMapConfig(defaultConfig.toggles, userConfig[`${defaultConfig.title}_Config`].toggles);
            const mergedConfig = {
                ...defaultConfig,
                ...userConfig,
                columns: mergedColumns,
                toggles: mergedToggles,
            }
            return mergedConfig;
        }
    }
    return defaultConfig;
}

function mergeGridMapConfig(defaultToggles, userToggles) {
    if (!userToggles) {
        userToggles = {}
    }
    return Object.keys(userToggles).length === 0 ? 
        defaultToggles : 
        {...defaultToggles, ...userToggles};
}

function getUniqueUserColumns(defaultColumns, userColumns){
  if(!defaultColumns){
    return [];
  }
  // Remove any duplicate columns from user configuration
  // & any columns removed from default grid configuration since they will no longer be mapped to any data
  const defaultColumnNames = defaultColumns.map(c => c.headerName); 
  const uniqueUserColumns = userColumns.reduce((columns, column) => {
    if(!columns.find(c => c.headerName === column.headerName) && defaultColumnNames.includes(column.headerName)){
      columns.push(column)
    }        
    return columns
  },[])
  return uniqueUserColumns;
}

function isColumnsPresent(columns) {
  return columns && Array.isArray(columns) && columns.length > 0;
}

function mergeColumnConfig(defaultColumns, userColumns) {
  // Filters out columns disabled in the default setup.
  // Use shouldForceHideColumn when a column’s hide state comes from a feature flag,
  // so user preferences don’t override and show it.
  const enabledDefaultColumns = defaultColumns?.filter(c => !c.shouldForceHideColumn);
    if(isColumnsPresent(userColumns) && isColumnsPresent(enabledDefaultColumns)){
        let mergedColumns = [];
        const uniqueUserColumns = getUniqueUserColumns(defaultColumns, userColumns)
        // Merge columns from userPreference and gridConifg , skip column removed from gridConfig
        uniqueUserColumns.forEach((userColumn) => {
            const defaultCol = enabledDefaultColumns.find((defaultCol) => defaultCol.headerName === userColumn.headerName);
            if (defaultCol) {
                if (defaultCol.children  && Array.isArray(defaultCol.children) && userColumn.children && Array.isArray(userColumn.children)) {
                    // If the column has children, merge them recursively
                    const mergedColumn = {
                      ...defaultCol,
                      ...userColumn,
                      children:  mergeColumnConfig(defaultCol.children, userColumn.children)
                    }
                    mergedColumns.push(mergedColumn)
                } else {
                  mergedColumns.push(merge(defaultCol, userColumn))
                }
            }
        });

        // Add new columns in gridConfig in their defined positions
        enabledDefaultColumns.forEach((defaultCol,index) => {
            if(mergedColumns.findIndex(mergedColumn => mergedColumn.headerName === defaultCol.headerName) < 0){
                mergedColumns.splice(index,0, {...defaultCol, hide:  defaultCol.hide ?? false})
            }
        });
        return mergedColumns;
    }
    return enabledDefaultColumns || [];
}

const getUTCDate = (dateString = Date.now()) => {
    const date = new Date(dateString);

    return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
    );
};

const getMinutesfromTimeString = (str) => {
    if (str) {
        const time = str.split(":");
        return time[0] * 60 + Number(time[1]);
    }
    return 0;
};

const getDateWithUtcHoursAndMinFromTimeString = (timeString) => {
    if (timeString) {
      const date = new Date();
      const [hours, mins] = timeString.split(":").map(Number);
      date.setUTCHours(hours);
      date.setUTCMinutes(mins);
      return date;
    }
    return null;
}

const formatDeliveryWindow = (params) => {
    if (params && params.data && params.value?.length > 0) {
      if (Array.isArray(params.value)) {
        const isMultiWindow = params.value.length > 1;
        return params.value
          ?.filter((deliverySlot) => {
            const newOption = {
              data: params.data,
              value: deliverySlot,
              isMultiWindow
            };
            return checkDeliveryWindowConstraints(newOption);
          })
          .join(" , ");
      } else {
        return checkDeliveryWindowConstraints(params);
      }
    }
  return params.value?.join?.(",") ?? "";
}

const checkDeliveryWindowConstraints = (params) => {
  if (!params.value) {
    return "";
  }
  if (
    !params.isMultiWindow && hasDefaultDeliveryWindow(params.value) &&
    !isAppointmentShipmentAndPriorityStartWithSGS(params.data)
  ) {
    return "";
  }
  return params.value;
};

const hasDefaultDeliveryWindow = (deliveryWindowValue) => {
  // delivery window ranges from 08:00 - 17:00
  const deliveryWindow = deliveryWindowValue.split("-");
  const startTime = getMinutesfromTimeString(deliveryWindow[0].trim());
  const endTime = getMinutesfromTimeString(deliveryWindow[1].trim());
  return startTime === 480 && endTime === 1020;
};

const isAppointmentShipmentAndPriorityStartWithSGS = (data) => {
  //not a priority shipment of type APT or SGS
  const { priority, apt } = data;
  const isAPT = apt ? true : false;
  return (priority && priority.startsWith("SGS")) || isAPT;
};

const getFormattedDeliveryWindowList = (params) => {
  return formatDeliveryWindow(params).split(" , ")
}

const getDeliveryWindowByEarlyAndLateCTA = (data) => {
  if (data?.earlyCta && data?.lateCta) {
    return `${data.earlyCta} - ${data.lateCta}`
  }
  return ""
}

const formatZeroValue = (params) => {
    if (params && params.value) {
        return params.value;
    }
    return "";
}

function suppressEnter(params) {
    const KEY_ENTER = 13;
    const { keyCode } = params.event || {};
    return keyCode === KEY_ENTER;
  }

function svcValueGetter(params) {
  const aptCodes = params.data.isApt ? [constant.SpecialNeedsStrings.APT] : [];
  const storedPriorities = toPriorityList(params.data.priority);
  return getDisplayServiceLevels(
    [...aptCodes, ...storedPriorities],
    params.context?.serviceLevelList
  ).join(", ");
}

function hazmatValueGetter(params) {
  if (params.data.isHazmat) {
    return [constant.SpecialNeedsStrings.HZ];
  } else {
    return [];
  }
}

const isValidTimeValue = (value) => {
    return value === constant.INVALID_TIME_RANGE;
}

//this will help to sort data containing alphanumeric value
const customAlphanumericComparator = (a, b) => {
  const isEmptyA = !a;
  const isEmptyB = !b;

  // Handle blank values by placing them at the top while sorting in increasing order
  if (isEmptyA && isEmptyB) {
    return 0;
  } else if (isEmptyA) {
    return -1;
  } else if (isEmptyB) {
    return 1;
  }
  const stringA = String(a);
  const stringB = String(b);

  return stringA.localeCompare(stringB, undefined, { numeric: true });
}

const customNumericComparator = (firstValue, secondValue) => {
  return Number(firstValue) - Number(secondValue);
}

const getGridOverlayString = (status, theme)=>{
  const bgColor = theme.gridOverlayBGColor;
  let textColor = theme.gridOverlayFontColor;
  const message = status.MESSAGE;
  if(status.VALUE === GRID_CONSTANTS.GRID_OVERLAY_STATUS.ERROR.VALUE){
    textColor = theme.KPI.KPINegative;
  }
  if (status === GRID_CONSTANTS.GRID_OVERLAY_STATUS.LOADING) {
    return `<span class="ag-overlay-loading-center" key='loading'>${message}</span>`;
  }
  return `<span key='non-loading' style="padding: 10px; border: 1px solid ${textColor}; background: ${bgColor};color:${textColor}">${message}</span>`
}

const formatDateWithDateFormat = (params, formatString) => {
  if (params && params.value) {
    return format(getUTCDate(params.value), formatString);
  }
  return "";
}

// Pressing the Space key causes continuous row toggle events (select/unselect),
// which in turn trigger multiple AG Grid and custom handlers, leading to infinite renders.
// To avoid this, we suppress repeated Space key events and combine this logic with any existing handler.

const suppressSpaceRepeat = (params) => {
  const event = params?.event;
  if (!event || params?.editing) return false; // Don't suppress space when in cell editor

  const isSpace = event.code === 'Space' || event.key === ' ';
  if (!isSpace) return false;

  if (event.repeat) {
    event.preventDefault();
    return true;
  }
  return false;
};

export const suppressKeyboardEventHandler = (existingHandler) => {
  return (params) => {
    return existingHandler?.(params) || suppressSpaceRepeat(params);
  };
};

export {
    formatDate,
    formatTime,
    formatDateTime,
    formatESTDateTime,
    formatCSTTimeStringToEST,
    minutesToTime,
    roundingNumbers,
    roundingNumbersWithoutTrailingZeros,
    mergeUserConfig,
    saveGridColumnState,
    setTimeFromDateTime,
    timeToMinutes,
    convertTimeToMinute,
    convertDateTimeToTimeString,
    isTimeValid,
    getUTCDate,
    getMinutesfromTimeString,
    formatDeliveryWindow,
    getFormattedDeliveryWindowList,
    getDeliveryWindowByEarlyAndLateCTA,
    suppressEnter,
    formatZeroValue,
    formatDateYear,
    formatDateTimeWithoutUTCConversion,
    formatMonthDateYear,
    formatMonthDateYearWithoutUTC,
    getDateWithUtcHoursAndMinFromTimeString,
    svcValueGetter,
    hazmatValueGetter,
    checkDeliveryWindowConstraints,
    isValidTimeValue,
    hasDefaultDeliveryWindow,
    customAlphanumericComparator,
    getGridOverlayString,
    customNumericComparator,
    formatDateWithDateFormat
};
